import React, {useState} from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Tooltip,
  Typography,
} from '@mui/material'
import {format, formatDistanceToNow} from 'date-fns'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import HistoryIcon from '@mui/icons-material/History'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import UpdateIcon from '@mui/icons-material/Update'
import RuleIcon from '@mui/icons-material/Rule'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import EditIcon from '@mui/icons-material/Edit'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import ReplayIcon from '@mui/icons-material/Replay'
import DownloadIcon from '@mui/icons-material/Download'
import IconButton from '@mui/material/IconButton'
import {downloadRulesCsv} from '../../../helpers/allocationRulesCsv'
import {useGetRuleHistoryQuery} from '../../../redux/commonApiCalls'

// ─── Constants ───────────────────────────────────────────────────────────────

const SOURCE_META = {
  RULE_CREATE_API:      {label: 'Rules Created',    color: '#2e7d32', bg: '#e8f5e9', Icon: AddCircleOutlineIcon},
  RULE_BULK_UPLOAD:     {label: 'Bulk Upload',       color: '#1565c0', bg: '#e3f2fd', Icon: CloudUploadIcon},
  RULE_UPDATE_API:      {label: 'Rule Updated',      color: '#e65100', bg: '#fff3e0', Icon: EditIcon},
  RULE_TYPE_CHANGE_API: {label: 'Rule Type Changed', color: '#6a1b9a', bg: '#f3e5f5', Icon: SwapHorizIcon},
}

const OPERATOR_LABEL = {
  EQUAL:        '=',
  GREATER_THAN: '>',
  LESS_THAN:    '<',
  GREATER_THAN_EQUAL: '≥',
  LESS_THAN_EQUAL:    '≤',
  IN:           'in',
  NOT_IN:       'not in',
  NOT_EQUAL:    '≠',
}

const RANK_COLORS = [
  {bg: '#fff8e1', color: '#f57f17', border: '#ffe082'}, // #1 gold
  {bg: '#f5f5f5', color: '#616161', border: '#e0e0e0'}, // #2 silver
  {bg: '#fbe9e7', color: '#bf360c', border: '#ffccbc'}, // #3 bronze
]

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatFact(fact = '', operator = '') {
  const isMin = ['GREATER_THAN', 'GREATER_THAN_EQUAL', '>', '>='].includes(operator)
  const isMax = ['LESS_THAN', 'LESS_THAN_EQUAL', '<', '<='].includes(operator)

  if (fact === 'WEIGHT') {
    if (isMin) return 'Min Weight'
    if (isMax) return 'Max Weight'
    return 'Weight'
  }
  if (fact === 'ORDER_VALUE') {
    if (isMin) return 'Min Invoice'
    if (isMax) return 'Max Invoice'
    return 'Invoice'
  }

  const MAP = {
    PAYMENT_MODE:  'Payment Mode',
    DELIVERY_TYPE: 'Delivery Type',
    CHANNEL_NAME:  'Channel',
  }
  return MAP[fact] || fact.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function formatValue(fact, value = '') {
  if (!value) return '—'
  if (fact === 'WEIGHT') return `${Number(value).toLocaleString()} kg`
  if (fact === 'ORDER_VALUE') return `₹${Number(value).toLocaleString()}`
  if (value.includes(',')) return value.split(',').map((v) => v.trim()).join(', ')
  return value
}

function getActiveCouriers(rule) {
  return (rule.result?.values || []).filter((v) => v && v.trim() !== '')
}

function formatCourierName(code = '') {
  return code.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

// ─── CSV Export ───────────────────────────────────────────────────────────────
// Uses the shared builder so this snapshot download is byte-identical to the
// Recommendation-Rules "Download CSV" and matches the ZONE_WEIGHT_RULE_UPLOAD template.

function exportRulesToCsv(record) {
  if (!record?.rules?.length) return
  const dateStr = record.changedAt
    ? format(new Date(record.changedAt), 'dd-MMM-yyyy_hh-mm-a')
    : 'snapshot'
  downloadRulesCsv(record.rules, `rule_snapshot_${dateStr}.csv`)
}

function detectDeliveryType(rule) {
  const cond = (rule.conditions || []).find((c) => c.fact === 'DELIVERY_TYPE')
  return cond?.value || null
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({icon, label, value, sub, color, bg}) {
  return (
    <Box sx={{
      flex: 1, minWidth: 150,
      border: '1px solid #e8e8e8',
      borderRadius: 2.5,
      p: 2,
      backgroundColor: '#fff',
      display: 'flex', gap: 1.5, alignItems: 'flex-start',
      boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
    }}>
      <Box sx={{
        width: 38, height: 38, borderRadius: 2,
        backgroundColor: bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {React.cloneElement(icon, {sx: {fontSize: 20, color}})}
      </Box>
      <Box sx={{minWidth: 0}}>
        <Typography sx={{color: '#9e9e9e', fontWeight: 600, fontSize: '0.67rem', textTransform: 'uppercase', letterSpacing: 0.5}}>
          {label}
        </Typography>
        <Typography sx={{fontWeight: 800, fontSize: '1.05rem', color: '#1a1a1a', lineHeight: 1.2, mt: 0.25}}>
          {value}
        </Typography>
        {sub && (
          <Typography sx={{color: '#bdbdbd', fontSize: '0.68rem', mt: 0.2}}>
            {sub}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

// ─── Condition Row ─────────────────────────────────────────────────────────────

function ConditionRow({fact, operator, value, isLast}) {
  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: '120px 1fr',
      alignItems: 'center',
      gap: 1,
      py: 0.75,
      borderBottom: isLast ? 'none' : '1px solid #f5f5f5',
    }}>
      <Typography sx={{fontSize: '0.73rem', color: '#424242', fontWeight: 600}}>
        {formatFact(fact, operator)}
      </Typography>
      <Typography sx={{fontSize: '0.73rem', color: '#1a1a1a', fontWeight: 500}}>
        {formatValue(fact, value)}
      </Typography>
    </Box>
  )
}

// ─── Courier Row ──────────────────────────────────────────────────────────────

function CourierRow({courier, rank}) {
  const style = RANK_COLORS[rank] || {bg: '#fafafa', color: '#616161', border: '#e0e0e0'}
  const isTop = rank < 3
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1,
      py: 0.6,
      borderBottom: '1px solid #f5f5f5',
      '&:last-child': {borderBottom: 'none'},
    }}>
      <Box sx={{
        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
        backgroundColor: isTop ? style.bg : '#f5f5f5',
        border: `1px solid ${isTop ? style.border : '#e0e0e0'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Typography sx={{fontSize: '0.62rem', fontWeight: 800, color: isTop ? style.color : '#9e9e9e'}}>
          {rank + 1}
        </Typography>
      </Box>
      <Typography sx={{
        fontSize: '0.75rem',
        fontWeight: rank === 0 ? 700 : 400,
        color: rank === 0 ? '#1a1a1a' : '#424242',
        flex: 1,
      }}>
        {formatCourierName(courier)}
      </Typography>
      {rank === 0 && (
        <Chip label='Primary' size='small' sx={{height: 16, fontSize: '0.6rem', fontWeight: 700, backgroundColor: '#fff8e1', color: '#f57f17'}} />
      )}
    </Box>
  )
}

// ─── Rule Card ────────────────────────────────────────────────────────────────

function RuleDetail({rule, ruleIndex}) {
  const couriers = getActiveCouriers(rule)
  const deliveryType = detectDeliveryType(rule)
  const otherConditions = (rule.conditions || []).filter((c) => c.fact !== 'DELIVERY_TYPE')

  return (
    <Box sx={{
      border: '1px solid #e8e8e8',
      borderRadius: 2,
      mb: 1.5,
      overflow: 'hidden',
      opacity: rule.active ? 1 : 0.6,
    }}>
      {/* Rule header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        px: 1.75, py: 1,
        backgroundColor: rule.active ? '#f8fffe' : '#f9f9f9',
        borderBottom: '1px solid #e8e8e8',
      }}>
        <Box sx={{
          width: 6, height: 6, borderRadius: '50%',
          backgroundColor: rule.active ? '#4caf50' : '#bdbdbd', flexShrink: 0,
        }} />
        <Typography sx={{fontWeight: 700, fontSize: '0.75rem', color: '#424242'}}>
          Rule {ruleIndex + 1}
        </Typography>
        {deliveryType && (
          <Chip
            icon={deliveryType === 'FORWARD'
              ? <LocalShippingIcon sx={{fontSize: '12px !important'}} />
              : <ReplayIcon sx={{fontSize: '12px !important'}} />}
            label={deliveryType}
            size='small'
            sx={{
              height: 20, fontSize: '0.65rem', fontWeight: 700,
              backgroundColor: deliveryType === 'FORWARD' ? '#e3f2fd' : '#fff3e0',
              color: deliveryType === 'FORWARD' ? '#1565c0' : '#e65100',
              '& .MuiChip-icon': {color: 'inherit'},
            }}
          />
        )}
        <Box sx={{ml: 'auto'}}>
          <Chip
            label={rule.active ? 'Active' : 'Inactive'}
            size='small'
            sx={{
              height: 18, fontSize: '0.62rem', fontWeight: 700,
              backgroundColor: rule.active ? '#e8f5e9' : '#f5f5f5',
              color: rule.active ? '#2e7d32' : '#9e9e9e',
            }}
          />
        </Box>
      </Box>

      {/* Body: two columns */}
      <Box sx={{display: 'flex', gap: 0, flexWrap: 'wrap'}}>

        {/* Left: Conditions */}
        <Box sx={{flex: '1 1 260px', px: 1.75, py: 1.25, borderRight: '1px solid #f0f0f0'}}>
          <Typography sx={{fontSize: '0.65rem', fontWeight: 700, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.75}}>
            Conditions
          </Typography>
          {otherConditions.length > 0 ? (
            otherConditions.map((c, i) => (
              <ConditionRow
                key={i}
                {...c}
                isLast={i === otherConditions.length - 1}
              />
            ))
          ) : (
            <Typography sx={{fontSize: '0.73rem', color: '#9e9e9e'}}>No specific conditions — applies to all orders</Typography>
          )}
        </Box>

        {/* Right: Courier priority */}
        <Box sx={{flex: '1 1 220px', px: 1.75, py: 1.25}}>
          <Typography sx={{fontSize: '0.65rem', fontWeight: 700, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.75}}>
            Courier Priority — {couriers.length} assigned
          </Typography>
          {couriers.length > 0 ? (
            couriers.map((c, i) => <CourierRow key={i} courier={c} rank={i} />)
          ) : (
            <Typography sx={{fontSize: '0.73rem', color: '#9e9e9e'}}>No couriers assigned</Typography>
          )}
        </Box>
      </Box>
    </Box>
  )
}

// ─── History Record ───────────────────────────────────────────────────────────

function HistoryRecord({record, isFirst}) {
  const [expanded, setExpanded] = useState(isFirst)
  const meta = SOURCE_META[record.changeSource] || {label: record.changeSource, color: '#555', bg: '#f5f5f5', Icon: HistoryIcon}
  const {Icon} = meta
  const activeRulesCount = (record.rules || []).filter((r) => r.active).length
  const totalCouriers = new Set((record.rules || []).flatMap((r) => getActiveCouriers(r))).size

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded((p) => !p)}
      elevation={0}
      disableGutters
      sx={{
        border: `1px solid ${isFirst ? '#bbdefb' : '#e8e8e8'}`,
        borderRadius: '10px !important',
        mb: 1.5,
        '&:before': {display: 'none'},
        overflow: 'hidden',
        boxShadow: isFirst ? '0 2px 8px rgba(21,101,192,0.08)' : 'none',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{fontSize: 18, color: '#757575'}} />}
        sx={{
          px: 2, minHeight: 60,
          backgroundColor: isFirst ? '#f5faff' : '#fff',
          '& .MuiAccordionSummary-content': {alignItems: 'center', gap: 1.5, my: 1.5},
        }}
      >
        <Avatar sx={{width: 34, height: 34, backgroundColor: meta.bg, flexShrink: 0}}>
          <Icon sx={{fontSize: 17, color: meta.color}} />
        </Avatar>

        <Box sx={{flex: 1, minWidth: 0}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <Typography sx={{fontWeight: 700, fontSize: '0.84rem', color: '#1a1a1a'}}>
              {meta.label}
            </Typography>
            {isFirst && (
              <Chip label='Current' size='small' sx={{height: 18, fontSize: '0.61rem', fontWeight: 700, backgroundColor: '#1565c0', color: '#fff'}} />
            )}
          </Box>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, flexWrap: 'wrap'}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
              <PersonOutlineIcon sx={{fontSize: 11, color: '#bdbdbd'}} />
              <Typography sx={{color: '#9e9e9e', fontSize: '0.7rem'}}>{record.changedBy}</Typography>
            </Box>
            <Typography sx={{color: '#bdbdbd', fontSize: '0.65rem', fontFamily: 'monospace', backgroundColor: '#f5f5f5', px: 0.75, py: 0.1, borderRadius: 1}}>
              {record.changeSource}
            </Typography>
          </Box>
        </Box>

        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, mr: 0.5}}>
          <Tooltip arrow title='Active rules'>
            <Box sx={{textAlign: 'center'}}>
              <Typography sx={{fontWeight: 800, fontSize: '0.9rem', color: '#2e7d32', lineHeight: 1}}>{activeRulesCount}</Typography>
              <Typography sx={{fontSize: '0.6rem', color: '#9e9e9e'}}>rules</Typography>
            </Box>
          </Tooltip>
          <Divider orientation='vertical' flexItem sx={{mx: 0.5}} />
          <Tooltip arrow title='Unique couriers'>
            <Box sx={{textAlign: 'center'}}>
              <Typography sx={{fontWeight: 800, fontSize: '0.9rem', color: '#6a1b9a', lineHeight: 1}}>{totalCouriers}</Typography>
              <Typography sx={{fontSize: '0.6rem', color: '#9e9e9e'}}>couriers</Typography>
            </Box>
          </Tooltip>
          <Divider orientation='vertical' flexItem sx={{mx: 0.5}} />
          <Box sx={{textAlign: 'right'}}>
            <Typography sx={{fontWeight: 600, fontSize: '0.72rem', color: '#424242', lineHeight: 1.2}}>
              {record.changedAt ? format(new Date(record.changedAt), 'dd MMM yyyy') : '—'}
            </Typography>
            <Typography sx={{fontSize: '0.66rem', color: '#9e9e9e'}}>
              {record.changedAt ? format(new Date(record.changedAt), 'hh:mm a') : ''}
            </Typography>
          </Box>
          <Divider orientation='vertical' flexItem sx={{mx: 0.5}} />
          <Tooltip arrow title='Export this snapshot to CSV'>
            <span>
              <IconButton
                size='small'
                disabled={!record.rules?.length}
                onClick={(e) => { e.stopPropagation(); exportRulesToCsv(record) }}
                sx={{color: '#1565c0', '&:hover': {backgroundColor: '#e3f2fd'}}}
              >
                <DownloadIcon sx={{fontSize: 17}} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{px: 2, py: 2, borderTop: '1px solid #f0f0f0', backgroundColor: '#fafafa'}}>
        {record.rules?.length > 0 ? (
          record.rules.map((rule, i) => (
            <RuleDetail key={rule.id} rule={rule} ruleIndex={i} />
          ))
        ) : (
          <Box sx={{py: 3, textAlign: 'center'}}>
            <SwapHorizIcon sx={{fontSize: 32, color: '#e0e0e0', mb: 0.5}} />
            <Typography sx={{fontSize: '0.8rem', color: '#9e9e9e'}}>
              Rules were cleared — the merchant's allocation type was switched.
            </Typography>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function RuleHistory({merchantOid}) {
  const {data: history = [], isLoading, isError} = useGetRuleHistoryQuery(
    {merchantOid},
    {skip: !merchantOid, refetchOnMountOrArgChange: true}
  )

  if (!merchantOid) return null

  if (isLoading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  if (isError) {
    return (
      <Box sx={{py: 4, textAlign: 'center'}}>
        <Typography variant='body2' color='error'>Failed to load rule history. Please try again.</Typography>
      </Box>
    )
  }

  if (history.length === 0) {
    return (
      <Box sx={{py: 8, textAlign: 'center'}}>
        <HistoryIcon sx={{fontSize: 44, color: '#e0e0e0', mb: 1, display: 'block', mx: 'auto'}} />
        <Typography variant='body2' sx={{color: '#9e9e9e'}}>No rule change history found for this merchant.</Typography>
      </Box>
    )
  }

  // ── KPIs ──────────────────────────────────────────────────────────────────
  const latest = history[0]
  const latestActiveRules = (latest?.rules || []).filter((r) => r.active).length
  const latestCouriers = new Set((latest?.rules || []).flatMap((r) => getActiveCouriers(r))).size
  const lastChangedAt = latest?.changedAt
    ? formatDistanceToNow(new Date(latest.changedAt), {addSuffix: true})
    : '—'
  const sourceCounts = history.reduce((acc, r) => {
    acc[r.changeSource] = (acc[r.changeSource] || 0) + 1
    return acc
  }, {})
  const topSource = Object.entries(sourceCounts).sort((a, b) => b[1] - a[1])[0]
  const topSourceLabel = topSource ? (SOURCE_META[topSource[0]]?.label || topSource[0]) : '—'

  return (
    <Box sx={{p: 2.5}}>
      {/* ── KPIs ── */}
      <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3}}>
        <KpiCard icon={<HistoryIcon />}     label='Total Changes'      value={history.length}        sub='Last 5 snapshots stored'                                              color='#1565c0' bg='#e3f2fd' />
        <KpiCard icon={<UpdateIcon />}      label='Last Modified'       value={lastChangedAt}         sub={latest?.changedAt ? format(new Date(latest.changedAt), 'dd MMM yyyy, hh:mm a') : ''} color='#e65100' bg='#fff3e0' />
        <KpiCard icon={<RuleIcon />}        label='Active Rules (Now)'  value={latestActiveRules}     sub={`${latestCouriers} unique courier${latestCouriers !== 1 ? 's' : ''} assigned`} color='#2e7d32' bg='#e8f5e9' />
        <KpiCard icon={<CloudUploadIcon />} label='Most Common Action'  value={topSourceLabel}        sub={topSource ? `${topSource[1]} time${topSource[1] !== 1 ? 's' : ''}` : ''} color='#6a1b9a' bg='#f3e5f5' />
      </Box>

      {/* ── Timeline header ── */}
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1.5}}>
        <Typography sx={{fontWeight: 700, fontSize: '0.82rem', color: '#424242'}}>Change Timeline</Typography>
        <Divider sx={{flex: 1}} />
        <Tooltip arrow title='Newest first. Each entry is a full snapshot of all rules at the time of that change.'>
          <Typography sx={{color: '#bdbdbd', cursor: 'default', fontSize: '0.75rem'}}>ⓘ</Typography>
        </Tooltip>
      </Box>

      {history.map((record, i) => (
        <HistoryRecord key={record.id} record={record} isFirst={i === 0} />
      ))}
    </Box>
  )
}
